import { useEffect, useMemo, useRef, useState } from 'react';
import TagFilter from '../components/TagFilter';
import ArtworkGrid from '../components/ArtworkGrid';
import { ALL_TAG, GalleryItem, ResolvedTag, loadTags } from '../data/gallery';
import { useGalleryItems } from '../hooks/useGalleryItems';
import { useLanguage } from '../contexts/LanguageContext';
import { translate } from '../i18n';

const introCopy: Record<'en' | 'zh', { kicker: string; paragraph: string }> = {
  en: {
    kicker: 'Where heritage meets brilliance',
    paragraph:
      'Sonsen presents a curated collection of crystal and jade masterworks, where centuries of Eastern artistry converge with exquisite modern craftsmanship. Each piece is captured in high-fidelity detail, inviting you to appreciate every facet of its radiance.'
  },
  zh: {
    kicker: '传承与华彩交相辉映',
    paragraph:
      '淞圣水晶甄选呈现翡翠珠宝与水晶艺术臻品，将百年东方工艺传承与匠心独具的现代设计完美融合。每件作品均以高清细节呈现，邀您细赏每一道璀璨光华。'
  }
};

const CollectionPage = () => {
  const { locale } = useLanguage();
  const [activeTags, setActiveTags] = useState<string[]>([ALL_TAG]);
  const [availableTags, setAvailableTags] = useState<ResolvedTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsError, setTagsError] = useState<Error | null>(null);

  const selectedTagIds = useMemo(
    () => activeTags.filter((tag) => tag !== ALL_TAG),
    [activeTags]
  );

  const { items, initialLoading, loadingMore, loadMore, hasMore, error } = useGalleryItems({
    tagIds: selectedTagIds
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setTagsLoading(true);
        const tags = await loadTags();
        if (!cancelled) {
          setAvailableTags(tags);
          setTagsError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setTagsError(err as Error);
        }
      } finally {
        if (!cancelled) {
          setTagsLoading(false);
        }
      }
    };

    load().catch(() => {
      /* handled above */
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (availableTags.length === 0) {
      return;
    }

    setActiveTags((prev) => {
      const validIds = new Set(availableTags.map((tag) => tag.id));
      const next = prev.filter((tag) => tag === ALL_TAG || validIds.has(tag));
      return next.length === 0 ? [ALL_TAG] : next;
    });
  }, [availableTags]);

  useEffect(() => {
    if (tagsError) {
      console.error('Failed to load tags from Payload CMS', tagsError);
    }
  }, [tagsError]);

  useEffect(() => {
    const node = sentinelRef.current;

    if (!node || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasMore && !initialLoading && !loadingMore) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '200px'
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, initialLoading, loadMore, loadingMore]);

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      if (tag === ALL_TAG) {
        return [ALL_TAG];
      }

      const withoutAll = prev.filter((item) => item !== ALL_TAG);
      const isActive = withoutAll.includes(tag);
      const next = isActive ? withoutAll.filter((item) => item !== tag) : [...withoutAll, tag];

      return next.length === 0 ? [ALL_TAG] : next;
    });
  };

  const displayedTags = tagsLoading ? [] : availableTags;

  const filteredArtworks = useMemo<GalleryItem[]>(() => {
    if (activeTags.includes(ALL_TAG)) {
      return items;
    }
    return items.filter((artwork) =>
      activeTags.every((tag) => artwork.tags.some((artworkTag) => artworkTag.id === tag))
    );
  }, [activeTags, items]);

  const intro = introCopy[locale];

  return (
    <div className="collection-page">
      <section className="collection-hero" id="about-section">
        <p className="collection-hero__kicker">{intro.kicker}</p>
        <h1>{translate(locale, 'collection')}</h1>
        <p className="collection-hero__body">{intro.paragraph}</p>
      </section>

      <TagFilter activeTags={activeTags} availableTags={displayedTags} onToggle={toggleTag} />

      {initialLoading ? (
        <p className="empty-state">{translate(locale, 'loadingState')}</p>
      ) : error ? (
        <p className="empty-state">{translate(locale, 'errorState')}</p>
      ) : filteredArtworks.length > 0 ? (
        <ArtworkGrid artworks={filteredArtworks} />
      ) : (
        <p className="empty-state">{translate(locale, 'emptyState')}</p>
      )}

      {hasMore ? <div ref={sentinelRef} aria-hidden style={{ height: 1 }} /> : null}

      {loadingMore && !initialLoading ? (
        <p className="empty-state">{translate(locale, 'loadingState')}</p>
      ) : null}
    </div>
  );
};

export default CollectionPage;
